import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { storiesApi } from '../api/stories';
import { storyRulesApi } from '../api/storyRules';
import { useSyncStoryContext } from '../hooks/useSyncStoryContext';
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning';
import StoryPicker from '../components/Common/StoryPicker';
import { SCOPE_KINDS, storyRulesPath } from '../utils/storyRulesPath';
import { EMPTY_PEOPLE_UNTIL_NOTE, STORY_RULES_LEDE, TRY_AGAIN } from '../utils/writerCopy';
import './StoryRulesPage.css';

const unwrapList = (res) => (Array.isArray(res) ? res : res?.data || []);

const byRecency = (a, b) => {
  const at = new Date(a.last_activity_at || a.created_at).getTime();
  const bt = new Date(b.last_activity_at || b.created_at).getTime();
  return bt - at;
};

const emptyDraft = () => ({
  sentence: '',
  scope_kind: 'book',
  person_entity_id: '',
  scene_section_id: '',
});

const EMPTY_PEOPLE_COPY = EMPTY_PEOPLE_UNTIL_NOTE;

function PersonSelect({ id, value, onChange, people }) {
  const empty = people.length === 0;
  return (
    <div className="sr-field">
      <label htmlFor={id}>Person</label>
      <select
        id={id}
        value={value}
        onChange={onChange}
        disabled={empty}
      >
        <option value="">{empty ? 'No people on this book' : 'Choose a person'}</option>
        {people.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>
      {empty && <p className="sr-hint" role="status">{EMPTY_PEOPLE_COPY}</p>}
    </div>
  );
}

/**
 * Story Rules room (#856). A fence, not a voice.
 * Add / edit / turn off / scope. Each rule is its own sentence.
 * Not a fifth box on a Style Card. Not the scene-writing page.
 */
export default function StoryRulesPage() {
  const { storyId: routeStoryId } = useParams();
  const { currentProject, currentStory, setProjectAndStory, storyListVersion } = useApp();
  const navigate = useNavigate();

  const openedId = routeStoryId || currentStory?.id || null;
  const { storyId, loadedStory } = useSyncStoryContext({ routeStoryId: openedId });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [projectStories, setProjectStories] = useState(null);
  const [rules, setRules] = useState([]);
  const [people, setPeople] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const loadAbortRef = useRef(null);

  const activeProjectId = currentProject?.id || loadedStory?.project_id || null;

  useEffect(() => {
    if (!activeProjectId) { setProjectStories(null); return undefined; }
    let cancelled = false;
    storiesApi.list(activeProjectId)
      .then((res) => { if (!cancelled) setProjectStories(unwrapList(res)); })
      .catch(() => { if (!cancelled) setProjectStories([]); });
    return () => { cancelled = true; };
  }, [activeProjectId, storyListVersion]);

  useEffect(() => {
    if (routeStoryId) { setPickerOpen(false); return; }
    if (currentStory?.id) {
      setPickerOpen(false);
      navigate(storyRulesPath(currentStory.id), { replace: true });
      return;
    }
    if (!currentProject) { setPickerOpen(true); return; }
    if (!projectStories || projectStories.length === 0) return;
    const mostRecent = [...projectStories].sort(byRecency)[0];
    setProjectAndStory(currentProject, mostRecent);
    navigate(storyRulesPath(mostRecent.id), { replace: true });
  }, [routeStoryId, currentStory?.id, currentProject, projectStories, navigate, setProjectAndStory]);

  const load = useCallback(async () => {
    if (!storyId) return;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const [list, scopes] = await Promise.all([
        storyRulesApi.list(storyId, { signal: controller.signal }),
        storyRulesApi.scopes(storyId, { signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return;
      const nextRules = list?.rules || [];
      setRules(nextRules);
      setPeople(scopes?.people || []);
      setScenes(scopes?.scenes || []);
      setEdits(Object.fromEntries(nextRules.map((rule) => [rule.id, {
        sentence: rule.sentence,
        scope_kind: rule.scope_kind,
        person_entity_id: rule.person_entity_id || '',
        scene_section_id: rule.scene_section_id || '',
      }])));
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setError(err?.message || 'Could not load Story Rules.');
    } finally {
      if (loadAbortRef.current === controller) setLoading(false);
    }
  }, [storyId]);

  useEffect(() => {
    load();
    return () => { loadAbortRef.current?.abort(); };
  }, [load]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err?.message || 'That Story Rule action failed.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const onAdd = async () => {
    const rule = await run(() => storyRulesApi.add(storyId, {
      sentence: draft.sentence,
      scopeKind: draft.scope_kind,
      personEntityId: draft.scope_kind === 'person' ? draft.person_entity_id || null : null,
      sceneSectionId: draft.scope_kind === 'scene' ? draft.scene_section_id || null : null,
    }));
    if (rule) {
      setDraft(emptyDraft());
      await load();
    }
  };

  const onSave = async (ruleId) => {
    const edit = edits[ruleId];
    if (!edit) return;
    const rule = await run(() => storyRulesApi.update(storyId, ruleId, {
      sentence: edit.sentence,
      scopeKind: edit.scope_kind,
      personEntityId: edit.scope_kind === 'person' ? edit.person_entity_id || null : null,
      sceneSectionId: edit.scope_kind === 'scene' ? edit.scene_section_id || null : null,
    }));
    if (rule) await load();
  };

  const onTurnOff = async (ruleId) => {
    const rule = await run(() => storyRulesApi.turnOff(storyId, ruleId));
    if (rule) await load();
  };

  const onTurnOn = async (ruleId) => {
    const rule = await run(() => storyRulesApi.turnOn(storyId, ruleId));
    if (rule) await load();
  };

  const patchEdit = (ruleId, patch) => {
    setEdits((prev) => ({
      ...prev,
      [ruleId]: { ...prev[ruleId], ...patch },
    }));
  };

  const newRuleDirty = draft.sentence !== ''
    || draft.scope_kind !== 'book'
    || draft.person_entity_id !== ''
    || draft.scene_section_id !== '';
  const editsDirty = rules.some((rule) => {
    const edit = edits[rule.id];
    if (!edit) return false;
    return edit.sentence !== rule.sentence
      || edit.scope_kind !== rule.scope_kind
      || (edit.person_entity_id || '') !== (rule.person_entity_id || '')
      || (edit.scene_section_id || '') !== (rule.scene_section_id || '');
  });
  useUnsavedChangesWarning(newRuleDirty || editsDirty);

  const handlePick = useCallback((project, story) => {
    setProjectAndStory(project, story);
    setPickerOpen(false);
    navigate(storyRulesPath(story.id));
  }, [setProjectAndStory, navigate]);

  if (pickerOpen) {
    return (
      <StoryPicker
        isOpen
        title="Open a book for Story Rules"
        scopedProject={currentProject}
        onClose={() => setPickerOpen(false)}
        onPick={handlePick}
      />
    );
  }

  if (!storyId) {
    return (
      <div className="sr-page">
        <p className="sr-empty">Select a book to add a Story Rule.</p>
      </div>
    );
  }

  return (
    <div className="sr-page">
      <header className="sr-header">
        <h1>Story Rules</h1>
        <p className="sr-header__lede">{STORY_RULES_LEDE}</p>
      </header>

      {error && (
        <div className="sr-error" role="alert">
          <p>{error}</p>
          <button type="button" className="sr-btn" onClick={() => load()}>{TRY_AGAIN}</button>
        </div>
      )}
      {loading && <p className="sr-empty">Loading Story Rules…</p>}

      <section className="sr-panel" aria-labelledby="sr-add-heading">
        <h2 id="sr-add-heading">Add a story rule</h2>
        <div className="sr-field sr-field--wide">
          <label htmlFor="sr-new-sentence">New sentence</label>
          <textarea
            id="sr-new-sentence"
            value={draft.sentence}
            onChange={(e) => setDraft((d) => ({ ...d, sentence: e.target.value }))}
            placeholder="Never let the narrator explain the weather."
          />
        </div>
        <div className="sr-row">
          <div className="sr-field">
            <label htmlFor="sr-new-scope">New scope</label>
            <select
              id="sr-new-scope"
              value={draft.scope_kind}
              onChange={(e) => setDraft((d) => ({ ...d, scope_kind: e.target.value }))}
            >
              {SCOPE_KINDS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          {draft.scope_kind === 'person' && (
            <PersonSelect
              id="sr-new-person"
              value={draft.person_entity_id}
              onChange={(e) => setDraft((d) => ({ ...d, person_entity_id: e.target.value }))}
              people={people}
            />
          )}
          {draft.scope_kind === 'scene' && (
            <div className="sr-field">
              <label htmlFor="sr-new-scene">Scene</label>
              <select
                id="sr-new-scene"
                value={draft.scene_section_id}
                onChange={(e) => setDraft((d) => ({ ...d, scene_section_id: e.target.value }))}
              >
                <option value="">Choose a scene</option>
                {scenes.map((s) => (
                  <option key={s.id} value={s.id}>{s.display_name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="sr-actions">
          <button type="button" className="sr-btn sr-btn--primary" disabled={busy} onClick={onAdd}>
            Add story rule
          </button>
        </div>
      </section>

      <section className="sr-panel" aria-labelledby="sr-list-heading">
        <h2 id="sr-list-heading">Rules on this book</h2>
        {rules.length === 0 && !loading ? (
          <p className="sr-empty">No Story Rules yet. Add a sentence.</p>
        ) : (
          <ul className="sr-list">
            {rules.map((rule) => {
              const edit = edits[rule.id] || {
                sentence: rule.sentence,
                scope_kind: rule.scope_kind,
                person_entity_id: rule.person_entity_id || '',
                scene_section_id: rule.scene_section_id || '',
              };
              return (
                <li
                  key={rule.id}
                  className={`sr-rule${rule.enabled ? '' : ' sr-rule--off'}`}
                >
                  <div className="sr-rule__meta">
                    <span className="sr-rule__state">{rule.enabled ? 'On' : 'Off'}</span>
                    <span>{rule.scope_label}</span>
                    {!rule.enabled && <span>This rule is off. The sentence stays.</span>}
                  </div>
                  <div className="sr-field sr-field--wide">
                    <label htmlFor={`sr-sentence-${rule.id}`}>Sentence</label>
                    <textarea
                      id={`sr-sentence-${rule.id}`}
                      value={edit.sentence}
                      onChange={(e) => patchEdit(rule.id, { sentence: e.target.value })}
                    />
                  </div>
                  <div className="sr-row">
                    <div className="sr-field">
                      <label htmlFor={`sr-scope-${rule.id}`}>Scope</label>
                      <select
                        id={`sr-scope-${rule.id}`}
                        value={edit.scope_kind}
                        onChange={(e) => patchEdit(rule.id, { scope_kind: e.target.value })}
                      >
                        {SCOPE_KINDS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    {edit.scope_kind === 'person' && (
                      <PersonSelect
                        id={`sr-person-${rule.id}`}
                        value={edit.person_entity_id}
                        onChange={(e) => patchEdit(rule.id, { person_entity_id: e.target.value })}
                        people={people}
                      />
                    )}
                    {edit.scope_kind === 'scene' && (
                      <div className="sr-field">
                        <label htmlFor={`sr-scene-${rule.id}`}>Scene</label>
                        <select
                          id={`sr-scene-${rule.id}`}
                          value={edit.scene_section_id}
                          onChange={(e) => patchEdit(rule.id, { scene_section_id: e.target.value })}
                        >
                          <option value="">Choose a scene</option>
                          {scenes.map((s) => (
                            <option key={s.id} value={s.id}>{s.display_name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="sr-actions">
                    <button
                      type="button"
                      className="sr-btn sr-btn--primary"
                      disabled={busy}
                      onClick={() => onSave(rule.id)}
                    >
                      Save
                    </button>
                    {rule.enabled ? (
                      <button
                        type="button"
                        className="sr-btn"
                        disabled={busy}
                        onClick={() => onTurnOff(rule.id)}
                      >
                        Turn off
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="sr-btn"
                        disabled={busy}
                        onClick={() => onTurnOn(rule.id)}
                      >
                        Turn on
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <p className="sr-hint">
          On and off stay with the sentence. Off is not delete.
          Rules join the voice later, on the scene-writing page, as
          one instruction. Never two.
        </p>
      </section>
    </div>
  );
}
